import React, { PureComponent, Fragment } from 'react'
import { array, bool, func, object, shape, string, PropTypes } from 'prop-types'
import ReactMarkdown from 'markdown-to-jsx'
import track from 'react-tracking'

import { CoremediaRenderer } from '@ycos/coremedia-renderer'
import BEMHelper from '@ynap/bem-helper'
import EmptyList from '@ynap/empty-list'
import { Button, SimpleAsyncButton } from '@ynap/button'
import { ImageWithSkeleton as Image } from '@ynap/image'
import Skeleton from '@ynap/skeleton'
import { linkRenderer } from '@ynap/router'
import Overlay from '@ynap/overlay'
import { getCookie } from '@ynap/redux-cookies-middleware/src/cookieApi'

import { deleteAccount } from './deleteApiUtils'
import {
    myaccountPageData,
    trackTermsAndPrivacyAnalytics,
    trackDataRequestsAnalytics,
    trackOpenDeleteModal,
    trackClickDelete,
    trackCancelDelete,
} from '../analytics'

const bem = new BEMHelper('PrivacySettings')

const openInNewTab = (event) => {
    event.preventDefault()
    window.open(event.target.href)
}

@track({})
export class PrivacySettings extends PureComponent {
    static propTypes = {
        messages: shape({
            privacy: shape({
                read: func,
                retryButton: func,
                pageError: func,
            }),
            privacyCloseAccount: shape({
                title: func,
                confirmationBody: func,
                acknowledgingBodyBp1: func,
                acknowledgingBodyBp2: func,
                confirmButton: func,
                cancelButton: func,
            }),
        }).isRequired,
        fetchContent: func.isRequired,
        fetchCountry: func.isRequired,
        isFetchingContent: bool,
        fetchContentError: bool,
        country: array,
        content: object,
        consentTime: string,
        fetchUser: func,
    }

    renderElement(element, localeId, config, index) {
        const components = [ConsentHeader, CustomerDataHeader, ConsentItem, CustomerInfo]
        const { messages, consentTime, country } = this.props

        switch (element.type) {
            case 'collection': {
                return element.items.map((subElement, index) => this.renderElement(subElement, localeId, config, index))
            }
            case 'externalLink':
            case 'placeholder':
                return (
                    <CustomerLink
                        type={element.type}
                        {...element}
                        messages={messages}
                        contentKey={element?.key}
                        config={config}
                        country={country[0]?.countryCode}
                    />
                )
            default:
                return CoremediaRenderer(element, components, {
                    index,
                    rules: [
                        (element) => {
                            const item = element.item
                            return { ...item, messages, consentTime, localeId, config }
                        },
                    ],
                })
        }
    }

    @track(
        myaccountPageData({
            name: 'privacy settings',
            type: 'privacy settings page',
            subCategory1: 'privacy settings',
        })
    )
    componentDidMount() {
        const { country, content, fetchContent, consentTime } = this.props

        if (country && !content) {
            fetchContent('consents-privacy-settings-collection', '', { mv: 'gitMarkdown', f: 'ycos' })
        }

        if (consentTime === undefined) {
            this.props.fetchUser()
        }
    }

    UNSAFE_componentWillUpdate(nextProps) {
        const { country, content, fetchContent, isFetchingContent, fetchContentError } = nextProps
        if (country && !content && !isFetchingContent && !fetchContentError) {
            fetchContent('consents-privacy-settings-collection', '', { mv: 'gitMarkdown', f: 'ycos' })
        }
    }

    render() {
        const { content, fetchContent, fetchContentError, messages, locale, config } = this.props

        if (fetchContentError) {
            return (
                <EmptyList
                    className={bem('emptyState')}
                    icon={<div className={bem('emptyImage')} />}
                    variant={'myAccountError'}
                    title={messages.privacy.pageError()}
                >
                    <Button
                        onClick={() => fetchContent('consents-privacy-settings-collection')}
                        type="primary-link"
                        className={bem('emptyLink')}
                    >
                        {messages.privacy.retryButton()}
                    </Button>
                </EmptyList>
            )
        }

        if (content) {
            return (
                <>
                    <div className={bem()}>
                        {content.items.map((element) => this.renderElement(element, locale.id, config))}
                    </div>
                </>
            )
        }

        return (
            <Fragment>
                <div className={bem('titleSkeleton')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('skeletonTwo')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('skeletonThree')}>
                    <Skeleton align={'left'} className={bem('skeletonBottom')} />
                    <div className={bem('imageSkeleton')} />
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('linksSkeleton')}>
                    <Skeleton className={bem('linkSkeleton')} />
                    <Skeleton className={bem('linkSkeleton')} />
                    <Skeleton className={bem('linkSkeleton')} />
                </div>
            </Fragment>
        )
    }
}
export default PrivacySettings

class ConsentHeader extends PureComponent {
    static layoutVariant = 'consent-title'

    static propTypes = {
        item: object,
    }

    render() {
        const { teaserTitle, teaserTextPlain } = this.props.item
        return (
            <Fragment>
                <h2 className={bem('privacy-title')}>{teaserTitle}</h2>
                <div className={bem('privacy-intro')}>
                    <p>{teaserTextPlain}</p>
                </div>
            </Fragment>
        )
    }
}

@track()
class ConsentItem extends PureComponent {
    static layoutVariant = 'consent-gdpr'

    static propTypes = {
        item: object,
        messages: object,
        consents: object,
        consentTime: string,
    }

    @track((props) => trackTermsAndPrivacyAnalytics(props.item))
    handleAnalyticsClick = () => {}

    render() {
        const { messages, item, consentTime, config, localeId } = this.props
        const { teaserTitle, subTitle } = item
        const internal_urls = ['account']
        const emailRegex = /^[^\s@]+@[^\s@]+$/
        const components = {
            a: ({ href, children }) =>
                href.search(emailRegex) !== -1 ? (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                ) : (
                    linkRenderer(config?.global?.urlConfig, internal_urls, localeId, href, children, (event) =>
                        openInNewTab(event)
                    )
                ),
        }

        return (
            <div className={bem('consent-item')}>
                <h4 className={bem('consent-title')}>{teaserTitle}</h4>
                {consentTime && <div className={bem('consent-info')}>{messages.privacy.read({ consentTime })}</div>}
                <div className={bem('consent-link')} onClick={this.handleAnalyticsClick}>
                    <ReactMarkdown options={{ overrides: components }}>{subTitle}</ReactMarkdown>
                </div>
            </div>
        )
    }
}

class CustomerDataHeader extends PureComponent {
    static layoutVariant = 'consent-image'

    static propTypes = {
        item: object,
    }

    render() {
        const { imageUrl, title, captionPlain } = this.props.item
        return (
            <div className={bem('customer-data-header')}>
                <div className={bem('customer-data-image-container')}>
                    <div className={bem('customer-data-image')}>
                        <Image src={imageUrl} alt={captionPlain} keepImageAspectRatio aspectRatio={'1:1'} />
                    </div>
                </div>
                <h3 className={bem('customer-data-title')}>{title}</h3>
            </div>
        )
    }
}

class CustomerInfo extends PureComponent {
    static layoutVariant = 'consent-item'

    static propTypes = {
        item: object,
    }

    render() {
        const { config, localeId } = this.props
        const { teaserText, teaserTitle } = this.props.item
        const internal_urls = ['account']
        const emailRegex = /^[^\s@]+@[^\s@]+$/
        const components = {
            a: ({ href, children }) =>
                href.search(emailRegex) !== -1 ? (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                ) : (
                    linkRenderer(config?.global?.urlConfig, internal_urls, localeId, href, children, (event) =>
                        openInNewTab(event)
                    )
                ),
        }

        return (
            <div className={bem('customer-info')}>
                {teaserTitle && <h4 className={bem('customer-info-title')}>{teaserTitle}</h4>}
                <ReactMarkdown options={{ overrides: components }}>{teaserText}</ReactMarkdown>
            </div>
        )
    }
}

@track()
class CustomerLink extends PureComponent {
    static propTypes = {
        href: string,
        teaser: {
            teaserTitle: string,
            teaserTextPlain: string,
        },
        layoutVariant: string,
        title: string,
        messages: object,
    }

    constructor(props) {
        super(props)
        this.handleCloseModal = this.handleCloseModal.bind(this)
        this.openModal = this.openModal.bind(this)
        this.handleAcknowledgingModal = this.handleAcknowledgingModal.bind(this)
        this.handleCloseAcknowledgingModal = this.handleCloseAcknowledgingModal.bind(this)
        this.state = { showCloseModal: false, showAcknowledgingModal: false, loading: false }
    }

    @track(() => trackCancelDelete())
    handleCloseModal() {
        this.setState({ showCloseModal: false })
    }

    @track(() => trackOpenDeleteModal())
    openModal() {
        this.setState({ showCloseModal: true })
    }

    @track(() => trackClickDelete())
    handleAcknowledgingModal({ baseUrl, storeIdentifier, ubertoken, clientId }) {
        this.setState({ showAcknowledgingModal: !this.state.showAcknowledgingModal })
        deleteAccount({ baseUrl, storeIdentifier, ubertoken, clientId })
        this.setState({ showCloseModal: false, loading: true })
    }

    handleCloseAcknowledgingModal() {
        this.setState({ showAcknowledgingModal: false, loading: false })
    }

    @track((props) => trackDataRequestsAnalytics(props))
    handleDataRequestsClick = () => {}

    render() {
        let {
            href,
            teaser: { teaserTitle, teaserTextPlain },
            contentKey,
            layoutVariant,
            title,
            messages,
            config,
            country,
            type,
        } = this.props
        const ubertoken = getCookie('ubertoken')
        const storeIdentifier = `${config.global.application.brandId}_${country.toLowerCase()}`
        const clientId = config.global.services.commerce.clientId
        const linkText = layoutVariant ? title : teaserTitle
        const baseUrl = this.props.config.global.services.commerce.baseUrl

        return (
            <>
                {type === 'placeholder' ? (
                    <div className={bem('customer-data-link', contentKey)}>
                        <a onClick={this.openModal}>{linkText}</a>
                    </div>
                ) : (
                    type === 'externalLink' &&
                    contentKey !== 'consent-link-delete' && (
                        <div className={bem('customer-data-link', contentKey)}>
                            <a href={`${href}${teaserTextPlain ? teaserTextPlain.trim() : ''}`}>{linkText}</a>
                        </div>
                    )
                )}
                {this.state.showCloseModal && (
                    <Overlay
                        title={messages.privacyCloseAccount.title()}
                        onClose={this.handleCloseModal}
                        handleBodyClick={this.handleCloseModal}
                        className={{ container: bem('overlayContainer'), content: bem('overlayContent') }}
                        type="alert"
                    >
                        <div className={bem('modal-body')}>
                            <p>{messages.privacyCloseAccount.confirmationBody()}</p>
                        </div>
                        <div className={bem('overlay-buttons-wrapper')}>
                            <SimpleAsyncButton
                                className={bem('confirmButton')}
                                type="primary"
                                copy={messages.privacyCloseAccount.confirmButton()}
                                onClick={() => {
                                    this.handleAcknowledgingModal({
                                        baseUrl,
                                        storeIdentifier,
                                        ubertoken,
                                        clientId,
                                    })
                                }}
                                loading={this.state.loading}
                            />
                            <Button
                                type="secondary"
                                onClick={this.handleCloseModal}
                                className={bem('closePrimaryButton')}
                            >
                                {messages.privacyCloseAccount.cancelButton()}
                            </Button>
                        </div>
                    </Overlay>
                )}

                {this.state.showAcknowledgingModal && (
                    <Overlay
                        title={messages.privacyCloseAccount.title()}
                        onClose={this.handleCloseAcknowledgingModal}
                        handleBodyClick={this.handleCloseAcknowledgingModal}
                        className={{ container: bem('overlayContainer'), content: bem('overlayContent') }}
                        type="alert"
                    >
                        <div className={bem('modal-body')}>
                            <p>{messages.privacyCloseAccount.acknowledgingBodyBp1()}</p>
                            <p>{messages.privacyCloseAccount.acknowledgingBodyBp2()}</p>
                        </div>
                    </Overlay>
                )}
            </>
        )
    }
}
